/* eslint-disable react/forbid-prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  FiAlertCircle,
  FiCalendar,
  FiFileText,
  FiMinus,
  FiPlus,
  FiSearch,
  FiUsers,
} from 'react-icons/fi';
import PackageDestinationField from './PackageDestinationField';
import PackageDropdown from '../common/PackageDropdown';
import PackagePaxSteppers from '../common/PackagePaxSteppers';
import PackageBookingLookup from '../PackageBookingLookup/PackageBookingLookup';
import { fetchPackageFacetsApi } from '../../api/packagesApi';
import { PACKAGE_ROUTES } from '../../constants/packageConstants';
import {
  DEFAULT_PACKAGE_PAX,
  budgetSteps,
  buildPackageSearchQuery,
  durationBounds,
  facetDestinations,
  formatPrice,
  normalizePax,
  paxError,
  paxSummary,
  sameDestination,
  totalPaxOf,
} from '../../utils/packageUtils';
import '../../styles/packagesShared.scss';
import './PackageSearchForm.scss';

const QUICK_PICKS = 5;

function DayStepper({ label, value, min, max, onChange }) {
  return (
    <div className="pkg-stepper-row">
      <div className="pkg-stepper-row__label">
        <strong>{label}</strong>
      </div>
      <div className="pkg-stepper">
        <button
          type="button"
          disabled={value <= min}
          onClick={() => onChange(Math.max(min, value - 1))}
          aria-label={`Fewer ${label.toLowerCase()}`}
        >
          <FiMinus size={14} />
        </button>
        <span>{value}</span>
        <button
          type="button"
          disabled={value >= max}
          onClick={() => onChange(Math.min(max, value + 1))}
          aria-label={`More ${label.toLowerCase()}`}
        >
          <FiPlus size={14} />
        </button>
      </div>
    </div>
  );
}

DayStepper.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

/**
 * Destination, trip length, travellers (by room sharing) and an optional
 * budget. Destinations, lengths and budgets come from the live catalogue
 * (available-facets), so nothing is offered that cannot match.
 *
 * variant="landing": the Packages tab of the landing page.
 * variant="modal":   inside "Modify search" (pass onSearch).
 */
function PackageSearchForm({ initialValues, onSearch, variant, autoOpen }) {
  const navigate = useNavigate();
  const [facetState, setFacetState] = useState({
    loading: true,
    ok: true,
    facets: null,
    read: null,
  });
  const [destination, setDestination] = useState(
    initialValues?.destination || '',
  );
  const [duration, setDuration] = useState({
    minDays: initialValues?.minDays ?? null,
    maxDays: initialValues?.maxDays ?? null,
  });
  const [maxPrice, setMaxPrice] = useState(initialValues?.maxPrice ?? null);
  const [pax, setPax] = useState(
    normalizePax(initialValues?.pax || DEFAULT_PACKAGE_PAX),
  );
  const [error, setError] = useState('');
  const [lookupOpen, setLookupOpen] = useState(false);

  const loadFacets = useCallback(() => {
    let alive = true;
    setFacetState((s) => ({ ...s, loading: true }));
    fetchPackageFacetsApi().then(({ ok, facets, read }) => {
      if (alive) setFacetState({ loading: false, ok, facets, read });
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => loadFacets(), [loadFacets]);

  const destinations = useMemo(
    () => facetDestinations(facetState.facets),
    [facetState.facets],
  );
  const budgets = useMemo(
    () => budgetSteps(facetState.facets),
    [facetState.facets],
  );
  const bounds = useMemo(
    () => durationBounds(facetState.facets),
    [facetState.facets],
  );

  const anyLength = duration.minDays == null && duration.maxDays == null;
  const minDays = duration.minDays ?? bounds.min;
  const maxDays = duration.maxDays ?? bounds.max;
  const currentPaxError = paxError(pax);

  const handleSubmit = () => {
    if (!destination.trim()) {
      setError('Please select a destination.');
      return;
    }
    if (currentPaxError) {
      setError(currentPaxError);
      return;
    }
    setError('');
    const next = {
      destination: destination.trim(),
      minDays: duration.minDays,
      maxDays: duration.maxDays,
      maxPrice,
      pax,
    };
    if (onSearch) {
      onSearch(next);
      return;
    }
    navigate(`${PACKAGE_ROUTES.RESULTS}?${buildPackageSearchQuery(next)}`);
  };

  return (
    <section className={`pkg-search pkg-search--${variant}`}>
      <div className="pkg-search__row">
        <div className="pkg-search__cell pkg-search__cell--dest">
          <PackageDestinationField
            value={destination}
            onChange={(d) => {
              setDestination(d);
              setError('');
            }}
            destinations={destinations}
            loading={facetState.loading}
            failed={!facetState.loading && !facetState.ok}
            onRetry={loadFacets}
            hasError={Boolean(error) && !destination}
            autoOpen={autoOpen}
          />
        </div>

        <div className="pkg-search__cell">
          <PackageDropdown
            icon={<FiCalendar />}
            label="Trip length"
            value={anyLength ? 'Any length' : `${minDays} - ${maxDays} days`}
            hint={`Packages run ${bounds.min} - ${bounds.max} days`}
            footer={
              !anyLength && (
                <button
                  type="button"
                  className="pkg-dropdown__reset"
                  onClick={() => setDuration({ minDays: null, maxDays: null })}
                >
                  Any length
                </button>
              )
            }
          >
            <DayStepper
              label="Minimum days"
              value={minDays}
              min={bounds.min}
              max={maxDays}
              onChange={(n) => setDuration({ minDays: n, maxDays })}
            />
            <DayStepper
              label="Maximum days"
              value={maxDays}
              min={minDays}
              max={bounds.max}
              onChange={(n) => setDuration({ minDays, maxDays: n })}
            />
          </PackageDropdown>
        </div>

        <div className="pkg-search__cell">
          <PackageDropdown
            icon={<FiUsers />}
            label="Travellers"
            value={paxSummary(pax)}
            hint={`${totalPaxOf(pax)} traveller${
              totalPaxOf(pax) === 1 ? '' : 's'
            } by room sharing`}
            hasError={Boolean(currentPaxError)}
            align="right"
          >
            <PackagePaxSteppers
              pax={pax}
              onChange={(next) => {
                setPax(next);
                setError('');
              }}
            />
            {currentPaxError && (
              <p className="pkg-field-error">{currentPaxError}</p>
            )}
          </PackageDropdown>
        </div>

        <button
          type="button"
          className="pkg-search__submit"
          onClick={handleSubmit}
        >
          <FiSearch />
          <span>Search Packages</span>
        </button>
      </div>

      {error && (
        <p className="pkg-search__error" role="alert">
          <FiAlertCircle /> {error}
        </p>
      )}

      <div className="pkg-search__bottom">
        <div className="pkg-search__quick">
          {budgets.length > 0 && (
            <>
              <span className="pkg-search__quick-label">Budget per person</span>
              <button
                type="button"
                className={`pkg-search__pill ${
                  !maxPrice ? 'pkg-search__pill--active' : ''
                }`}
                aria-pressed={!maxPrice}
                onClick={() => setMaxPrice(null)}
              >
                Any budget
              </button>
              {budgets.map((b) => (
                <button
                  type="button"
                  key={b}
                  className={`pkg-search__pill ${
                    maxPrice === b ? 'pkg-search__pill--active' : ''
                  }`}
                  aria-pressed={maxPrice === b}
                  onClick={() => setMaxPrice(b)}
                >
                  Under {formatPrice(b)}
                </button>
              ))}
            </>
          )}
          {budgets.length === 0 && destinations.length > 0 && (
            <>
              <span className="pkg-search__quick-label">Popular</span>
              {destinations.slice(0, QUICK_PICKS).map((d) => {
                const active = sameDestination(d.value, destination);
                return (
                  <button
                    type="button"
                    key={d.value}
                    className={`pkg-search__pill ${
                      active ? 'pkg-search__pill--active' : ''
                    }`}
                    aria-pressed={active}
                    onClick={() => {
                      setDestination(d.value);
                      setError('');
                    }}
                  >
                    {d.label}
                  </button>
                );
              })}
            </>
          )}
        </div>

        {variant === 'landing' && (
          <button
            type="button"
            className="pkg-search__lookup"
            onClick={() => setLookupOpen(true)}
          >
            <FiFileText />
            Find a package booking
          </button>
        )}
      </div>

      {variant === 'landing' && (
        <PackageBookingLookup
          show={lookupOpen}
          onHide={() => setLookupOpen(false)}
        />
      )}
    </section>
  );
}

PackageSearchForm.propTypes = {
  initialValues: PropTypes.object,
  onSearch: PropTypes.func,
  variant: PropTypes.oneOf(['landing', 'modal']),
  autoOpen: PropTypes.bool,
};

PackageSearchForm.defaultProps = {
  initialValues: null,
  onSearch: null,
  variant: 'landing',
  autoOpen: false,
};

export default PackageSearchForm;
